/**
 * Shared shell style for content rendered inside a task step drawer (Model
 * Configuration, Data Editor, Training, Evaluations). Mirrors the spacing in
 * the Figma "Data Editor" reference so every screen sits at the same offsets
 * from the drawer chrome instead of each one defining its own padding.
 *
 * Apply via `style={getTaskDrawerShellStyle(isDrawerFullWidth)}` on the outermost
 * scrolling wrapper of the drawer screen (or `TASK_DRAWER_SHELL_STYLE` when
 * full-width mode does not apply). The frozen default keeps inline-style identity
 * stable across renders; writes would silently desync the screens.
 */
/** @param {boolean} [isFullWidth] TaskPage drawer expand mode — wider horizontal inset. */
export const getTaskDrawerShellStyle = (isFullWidth = false) => ({
    margin: 0,
    padding: isFullWidth ? '24px 15%' : '24px',
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
});

export const TASK_DRAWER_SHELL_STYLE = Object.freeze(getTaskDrawerShellStyle(false));

/** Max width (px) of the task step drawer panel (Spark DrawerItem). */
export const TASK_DRAWER_MAX_WIDTH_PX = 917;

/**
 * `rootClassName` for the nested ModelDetailsDrawer (still Ant Design).
 * Drives `.ant-drawer.task-drawer-panel-width` CSS width overrides.
 */
export const TASK_NESTED_MODEL_DRAWER_ROOT_CLASS_NAME = 'drawer-below-navbar task-drawer-panel-width task-drawer-nested-model-details';

/** Applied to `document.body` while model details is open over the task drawer. */
export const TASK_NESTED_MODEL_DRAWER_BODY_CLASS = 'task-nested-model-drawer-open';

/** Matches TaskPage evaluation drawer mask. */
export const TASK_DRAWER_MASK_STYLE = Object.freeze({
    background: 'rgba(15, 23, 42, 0.45)',
});

/**
 * Maps TASK_ACTIONS screenKeys to Spark DrawerToolbarStepId values.
 * @type {Record<string, import('@spark').DrawerToolbarStepId>}
 */
export const SCREEN_KEY_TO_STEP_ID = Object.freeze({
    'model-setup': 'model-configuration',
    'data-selection': 'data-overview',
    'train': 'training',
    'evaluation-from-session': 'evaluations',
});

/** Inverse of SCREEN_KEY_TO_STEP_ID — maps DrawerToolbarStepId back to a screenKey. */
export const STEP_ID_TO_SCREEN_KEY = Object.freeze(
    Object.fromEntries(Object.entries(SCREEN_KEY_TO_STEP_ID).map(([k, v]) => [v, k]))
);
